import { useEffect } from 'react';
import { useFormikContext } from 'formik';

import { useTiclickTableContext } from '@src/design-system/molecules/ticlick-table/context/ticlick-table-context-hook';
import { Textfield } from '@src/shared/fields/text-field';
import { SavedViewsSelect } from '@src/shared/features/views/components/saved-views-select';
import type { TSubmission } from '@shared/types/submission';
import type { TStudent } from '@shared/types/student';

import type { TCreateReminderFormValues } from '../schema';
import {
  getMemberIdFromRow,
  SelectedParticipantsPopover,
} from './selected-participants-popover';

type TParticipantRow = TSubmission | TStudent;

function toMemberIdsKey(ids: number[]) {
  return [...ids].sort((a, b) => a - b).join(',');
}

export function ParticipantsAudienceSection() {
  const { table } = useTiclickTableContext<TParticipantRow>();
  const { setFieldValue } = useFormikContext<TCreateReminderFormValues>();

  const selectedRows = table
    .getSelectedRowModel()
    .rows.flat()
    .map((row) => row.original as TParticipantRow);

  const selectedMemberIdsKey = toMemberIdsKey(
    selectedRows
      .map(getMemberIdFromRow)
      .filter((id): id is number => id != null)
  );

  useEffect(() => {
    const nextIds = selectedMemberIdsKey
      ? selectedMemberIdsKey.split(',').map(Number)
      : [];

    setFieldValue('selectedMemberIds', nextIds, false);
  }, [selectedMemberIdsKey, setFieldValue]);

  const hasPreselectedParticipants = selectedRows.length > 0;

  return (
    <section className='rounded-xl border border-[#E8E7F0] bg-white p-4'>
      <p className='mb-4 text-sm font-semibold text-gray-500'>
        1. Audience Details
      </p>

      <div className='flex flex-col gap-4'>
        <Textfield
          label='Reminder name'
          name='name'
          placeholder='Enter Reminder Name'
        />

        {hasPreselectedParticipants ? (
          <SelectedParticipantsPopover />
        ) : (
          <SavedViewsSelect
            appliedAll={{ APPLICATION: 'All Participants' }}
            entity_type='APPLICATION'
          />
        )}
      </div>
    </section>
  );
}
